import React, { useState, useEffect } from "react";
import { getToken } from '../utils/cookieUtils';
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer
} from "@paypal/react-paypal-js";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import toast from "react-hot-toast";

const PaypalButtonComponent = ({ onSuccess, onError }) => {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  const [isMobileRedirect, setIsMobileRedirect] = useState(false);

  // Enhanced mobile and in-app browser detection
  const isInAppBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /FBAN|FBAV|Instagram|WhatsApp|Twitter|Line|WeChat|LinkedIn|Snapchat/i.test(ua) ||
           window.webkit?.messageHandlers ||
           window.Android ||
           (window.webkit && window.webkit.messageHandlers);
  };

  const isMobileDevice = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
           window.innerWidth <= 768;
  };

  // Handle mobile redirect issues
  useEffect(() => {
    if (isMobileDevice() || isInAppBrowser()) {
      setIsMobileRedirect(true);
    }
  }, []);

  // ✅ 1. Create Vault Setup Token
 const getSetupToken = async () => {
  try {
    const currentUrl = window.location.href; // 👈 dynamic URL

    const res = await fetch(
      `${process.env.REACT_APP_BASE_URL}user/customer_token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          user_id: localStorage.getItem("logged_user_id"),
          url: currentUrl 
        })
      }
    );

    const data = await res.json();
    if (!data.success)
      throw new Error(data.message || "Failed to get setup token");

    return data.data.id;
  } catch (error) {
    //console.error("Setup token error:", error);
    throw error;
  }
};
  // ✅ 2. Use vaultSetupToken dynamically here
  const handleApprove = async ({ vaultSetupToken }) => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_BASE_URL}user/create_payment_token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          setup_token: vaultSetupToken,
          user_id: localStorage.getItem("logged_user_id")
        })
      }
    );

    const result = await res.json();
    //console.log("Response from create_payment_token:", result);

    // Clear loading toast if it exists
    toast.dismiss("paypal-loading");

    // ✅ Success check - only check for success flag
    if (result?.success) {
      // ✅ Success Toast
      toast.success("Your Payment Method Saved Successfully!");

      if (isMobileRedirect) {
        console.log("PayPal account connected successfully!");
      }

      // Optional success callback
      onSuccess?.(result);
      return;
    }

    // ❌ Error case
    console.error("Payment token creation failed:", result);
    if (isMobileRedirect) {
      toast.error("Payment setup failed. Please try again.");
    }

    // Optional error callback
    onError?.(result);

  } catch (err) {
    //console.error("Payment token error:", err);

    // Clear loading toast if it exists
    toast.dismiss("paypal-loading");

    if (isMobileRedirect) {
      toast.error("Payment setup failed. Please try again.");
    } else {
      console.log(err.message || "Failed to save payment method");
    }

    // Optional error callback
    onError?.(err);
  }
};
  if (isRejected)
    return (
      <Typography color="error" align="center">
        Failed to load PayPal.
      </Typography>
    );

  if (isPending)
    return <Typography align="center">Loading PayPal button...</Typography>;

  // Show enhanced mobile warning for in-app browsers
  if (isInAppBrowser()) {
    return (
      <Box>
        <Typography align="center" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
          ⚠️ Browser Compatibility Issue
        </Typography>
        <Typography align="center" sx={{ mb: 2 }}>
          Please open this page in Chrome or Safari to continue with PayPal.
        </Typography>
        <Typography align="center" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          In-app browsers (Instagram, Facebook, WhatsApp, etc.) may cause PayPal login issues.
        </Typography>
        <Typography align="center" variant="body2" color="primary">
          💡 Copy this link and open in your phone's browser: {window.location.href}
        </Typography>
      </Box>
    );
  }

  return (
    <PayPalButtons
      fundingSource="paypal"
      style={{
        layout: "vertical",
        shape: "pill",
        label: "paypal",
        height: 45,
        disableMaxWidth: true
      }}
      createVaultSetupToken={getSetupToken}
      onApprove={handleApprove}
      onCancel={() => {
        //console.log("User cancelled PayPal setup");
        if (isMobileRedirect) {
          toast.error("PayPal setup was cancelled. Please try again.");
        }
      }}
      onError={(err) => {
        //console.error("PayPal error:", err);
        //console.log("PayPal error occurred");
        
        // Mobile-specific error handling
        if (isMobileRedirect) {
          toast.error("Payment setup failed. Please try again.");
        }
        
        onError?.(err);
      }}
      onClick={() => {
        // Handle mobile click to prevent redirect issues
        if (isMobileRedirect) {
          //console.log("Mobile PayPal flow initiated");
         // toast.loading("Opening PayPal...", { id: "paypal-loading" });
        }
      }}
    />
  );
};

export default function PaypalVaultSection({ onPaymentSaved, onError }) {
  const initialOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    currency: "USD",
    components: "buttons,card-fields",
    vault: true,
    intent: "capture",
    "disable-funding": ""
  };

  return (
    <Box mt={2}>
      <PayPalScriptProvider options={initialOptions}>
        <PaypalButtonComponent
          onSuccess={onPaymentSaved}
          onError={onError}
        />
      </PayPalScriptProvider>
    </Box>
  );
}